// src/components/Properties/ShareModal.jsx
// ======================================================
// 🔥 SHARE MODAL ULTRA PREMIUM — NIVEL AIRBNB/ZILLOW
// V3 ENTERPRISE PORTAL
// ======================================================
// Features:
// - WhatsApp con mensaje pre-formateado
// - Copiar link con animación de confirmación
// - Email con asunto + cuerpo pre-rellenado
// - QR Code generado al instante (canvas)
// - Facebook, Twitter, Telegram
// - Glassmorphism + animaciones GPU-accelerated
// - V3: Portal a document.body · Dark mode automático
// ======================================================

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Copy, Check, Share2, MessageCircle, Mail, Link2,
  QrCode, Facebook, Send, Smartphone, ChevronRight
} from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { useTheme } from '../../contexts/ThemeContext';
import { usePortal, useBodyScrollLock, useEscapeKey, portalize } from '../../utils/portalUtils';

// ======================================================
// 🔧 UTILIDADES INTERNAS
// ======================================================

const formatShareMessage = (property, url) => {
  const title = property?.title || 'Imóvel';
  const price = property?.price
    ? `R$ ${Number(property.price).toLocaleString('pt-BR')}`
    : 'Valor sob consulta';
  const address = property?.address || property?.neighborhood || '';
  const quartos = property?.bedrooms ? `\n🛏️ ${property.bedrooms} quarto${property.bedrooms > 1 ? 's' : ''}` : '';
  const area = property?.area ? `\n📐 ${property.area}m²` : '';

  return `🏡 *${title}*\n💰 ${price}${quartos}${area}\n📍 ${address}\n\n🔗 ${url}`;
};

const getWhatsAppUrl = (property, url) => {
  const message = encodeURIComponent(formatShareMessage(property, url));
  return `https://wa.me/?text=${message}`;
};

const getEmailUrl = (property, url) => {
  const subject = encodeURIComponent(`Imóvel: ${property?.title || 'Imóvel'}`);
  const body = encodeURIComponent(
    `Olá!\n\nVi este imóvel e fiquei interessado:\n\n` +
    `${property?.title || 'Imóvel'}\n` +
    `${property?.price ? `Valor: R$ ${Number(property.price).toLocaleString('pt-BR')}\n` : ''}` +
    `${property?.address ? `Endereço: ${property.address}\n` : ''}` +
    `\nLink: ${url}\n\n` +
    `Poderia me passar mais informações?`
  );
  return `mailto:?subject=${subject}&body=${body}`;
};

const getFacebookUrl = (url) => {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
};

const getTwitterUrl = (property, url) => {
  const text = encodeURIComponent(`${property?.title || 'Imóvel'} - ${url}`);
  return `https://twitter.com/intent/tweet?text=${text}`;
};

const getTelegramUrl = (property, url) => {
  const text = encodeURIComponent(formatShareMessage(property, url));
  return `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${text}`;
};

// ======================================================
// 🔲 GENERADOR DE QR CODE (PURO, SIN LIBRERÍAS)
// ======================================================
const generateQRCode = (text, size = 200) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&margin=10`;
    img.onload = () => {
      ctx.drawImage(img, 0, 0, size, size);
      resolve(canvas.toDataURL());
    };
    img.onerror = () => resolve(null);
  });
};

// ======================================================
// 🎬 ANIMACIONES
// ======================================================

const OVERLAY_ANIMATION = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2 }
};

const DESKTOP_PANEL_ANIMATION = {
  initial: { opacity: 0, scale: 0.9, y: 20 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.9, y: 20 },
  transition: { type: 'spring', stiffness: 400, damping: 30 }
};

const MOBILE_SHEET_ANIMATION = {
  initial: { y: '100%' },
  animate: { y: 0 },
  exit: { y: '100%' },
  transition: { type: 'spring', damping: 30, stiffness: 300 }
};

// ======================================================
// 🎨 SHARE MODAL COMPONENT
// ======================================================
export default function ShareModal({ isOpen, onClose, property, url, primaryColor = '#2563eb', title = 'Compartilhar' }) {
  const isMobile = useIsMobile();
  const { isDark } = useTheme();
  const mounted = usePortal();

  // ✅ Portal hooks
  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onClose);

  const [copied, setCopied] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [activeTab, setActiveTab] = useState('apps');
  const qrGeneratedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !qrGeneratedRef.current && url) {
      qrGeneratedRef.current = true;
      generateQRCode(url).then(setQrCode);
    }
    if (!isOpen) {
      qrGeneratedRef.current = false;
      setQrCode(null);
    }
  }, [isOpen, url]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  const shareOptions = useMemo(() => [
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-green-500 hover:bg-green-600 active:bg-green-700',
      iconColor: 'text-green-500',
      action: () => window.open(getWhatsAppUrl(property, url), '_blank'),
      tab: 'apps'
    },
    {
      id: 'copy',
      label: copied ? 'Copiado!' : 'Copiar link',
      icon: copied ? Check : Copy,
      color: copied ? 'bg-emerald-500' : 'bg-slate-700 hover:bg-slate-800 active:bg-slate-900',
      iconColor: copied ? 'text-emerald-500' : 'text-slate-600',
      action: handleCopyLink,
      tab: 'apps'
    },
    {
      id: 'email',
      label: 'Email',
      icon: Mail,
      color: 'bg-rose-500 hover:bg-rose-600 active:bg-rose-700',
      iconColor: 'text-rose-500',
      action: () => window.open(getEmailUrl(property, url), '_blank'),
      tab: 'apps'
    },
    {
      id: 'telegram',
      label: 'Telegram',
      icon: Send,
      color: 'bg-sky-500 hover:bg-sky-600 active:bg-sky-700',
      iconColor: 'text-sky-500',
      action: () => window.open(getTelegramUrl(property, url), '_blank'),
      tab: 'apps'
    },
    {
      id: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      color: 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800',
      iconColor: 'text-blue-600',
      action: () => window.open(getFacebookUrl(url), '_blank'),
      tab: 'social'
    },
    {
      id: 'twitter',
      label: 'Twitter',
      icon: () => (
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      color: 'bg-black hover:bg-gray-800 active:bg-gray-900',
      iconColor: 'text-black',
      action: () => window.open(getTwitterUrl(property, url), '_blank'),
      tab: 'social'
    },
  ], [property, url, copied, handleCopyLink]);

  const filteredOptions = useMemo(() =>
    shareOptions.filter(opt => opt.tab === activeTab),
    [shareOptions, activeTab]
  );

  // 🌓 Clases dark mode
  const bgModal = "bg-white dark:bg-slate-900";
  const bgSheet = "bg-white dark:bg-slate-900";
  const textDark = "text-slate-800 dark:text-slate-100";
  const textGray = "text-slate-500 dark:text-slate-400";
  const textGrayStrong = "text-slate-600 dark:text-slate-300";
  const textGrayStronger = "text-slate-700 dark:text-slate-200";
  const bgSubtle = "bg-slate-50 dark:bg-slate-800/50";
  const bgSubtleHover = "hover:bg-slate-100 dark:hover:bg-slate-800";
  const bgQR = "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-600";
  const bgQRIcon = "bg-slate-200 dark:bg-slate-700";
  const bgQRIconHover = "group-hover:bg-slate-300 dark:group-hover:bg-slate-600";
  const bgCopyBtn = "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600";
  const bgPulse = "bg-slate-100 dark:bg-slate-800";
  const handleBar = "bg-gray-300 dark:bg-slate-600";
  const btnClose = "hover:bg-slate-100 dark:hover:bg-slate-800";
  const tabInactive = "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800";
  const labelColor = "text-slate-600 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200";

  const sharedContent = (
    <>
      {/* Header */}
      <div className={isMobile ? "px-5 pb-3" : "relative p-6 pb-4"}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${primaryColor}15` }}
            >
              <Share2 className="w-5 h-5" style={{ color: primaryColor }} />
            </div>
            <div>
              <h2 className={`text-lg font-bold ${textDark}`}>{title}</h2>
              <p className={`text-xs ${textGray}`}>Compartilhe este imóvel</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-full transition-colors active:scale-95 flex-shrink-0 ${btnClose}`}
            aria-label="Fechar"
          >
            <X className={`w-5 h-5 ${textGray}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 ${isMobile ? 'px-5 pb-3' : 'px-6 pb-2'}`}>
        {[
          { id: 'apps', label: 'Aplicativos' },
          { id: 'social', label: 'Redes Sociais' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium rounded-xl transition-all active:scale-95 ${
              activeTab === tab.id
                ? 'text-white shadow-md'
                : tabInactive
            }`}
            style={activeTab === tab.id ? { backgroundColor: primaryColor } : {}}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Grid de opciones */}
      <div className={isMobile ? "px-5 py-3" : "px-6 py-4"}>
        <div className="grid grid-cols-4 gap-3">
          {filteredOptions.map((option) => {
            const Icon = option.icon;
            return (
              <motion.button
                key={option.id}
                whileHover={isMobile ? {} : { scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
                onClick={option.action}
                className="flex flex-col items-center gap-2 group"
              >
                <div
                  className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all shadow-md ${
                    isMobile ? 'active:shadow-lg' : 'group-hover:shadow-lg'
                  } ${option.color}`}
                >
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <span className={`text-xs font-medium ${labelColor} transition-colors`}>
                  {option.label}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* QR Code Section */}
      <div className={isMobile ? "px-5 pb-3" : "px-6 pb-2"}>
        <button
          onClick={() => setActiveTab(activeTab === 'qr' ? 'apps' : 'qr')}
          className={`w-full flex items-center justify-between p-3.5 ${bgSubtle} ${bgSubtleHover} rounded-xl transition-colors`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${bgQRIcon} ${bgQRIconHover} rounded-lg flex items-center justify-center transition-colors`}>
              <QrCode className={`w-5 h-5 ${textGrayStrong}`} />
            </div>
            <div className="text-left">
              <p className={`text-sm font-medium ${textGrayStronger}`}>QR Code</p>
              <p className={`text-xs ${textGray}`}>
                {isMobile ? 'Escaneie para acessar' : 'Escaneie para acessar o imóvel'}
              </p>
            </div>
          </div>
          <ChevronRight className={`w-5 h-5 text-slate-400 dark:text-slate-500 transition-transform ${activeTab === 'qr' ? 'rotate-90' : ''}`} />
        </button>
      </div>

      {/* QR Code expandido */}
      <AnimatePresence>
        {activeTab === 'qr' && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className={`${isMobile ? 'px-5 pb-5' : 'px-6 pb-6'} flex flex-col items-center`}>
              <div className={`${bgQR} border-2 rounded-2xl p-3 shadow-inner`}>
                {qrCode ? (
                  <img
                    src={qrCode}
                    alt="QR Code"
                    className={`${isMobile ? 'w-44 h-44' : 'w-48 h-48'} rounded-xl`}
                  />
                ) : (
                  <div className={`${isMobile ? 'w-44 h-44' : 'w-48 h-48'} ${bgPulse} rounded-xl flex items-center justify-center animate-pulse`}>
                    <QrCode className="w-8 h-8 text-slate-300 dark:text-slate-600" />
                  </div>
                )}
              </div>
              <p className={`text-xs ${textGray} mt-3 text-center`}>
                {isMobile ? 'Aponte a câmera do celular' : 'Aponte a câmera do celular para o QR code'}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer con link */}
      <div className={isMobile ? "px-5 pb-6" : "px-6 pb-6"}>
        <div className={`flex items-center gap-2 p-3 ${bgSubtle} rounded-xl`}>
          <Link2 className={`w-4 h-4 ${textGray} flex-shrink-0`} />
          <p className={`text-xs ${textGray} truncate flex-1`}>{url}</p>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={handleCopyLink}
            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              copied
                ? 'bg-emerald-500 text-white'
                : bgCopyBtn
            }`}
          >
            {copied ? 'Copiado!' : 'Copiar'}
          </motion.button>
        </div>
      </div>
    </>
  );

  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence mode="wait">
      <motion.div
        {...OVERLAY_ANIMATION}
        className={`fixed inset-0 z-[99999] flex ${
          isMobile 
            ? 'items-end bg-black/70 dark:bg-black/85' 
            : 'items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4'
        }`}
        onClick={onClose}
        aria-modal="true"
        role="dialog"
        aria-label={title}
      >
        {isMobile ? (
          // 📱 MOBILE: BOTTOM SHEET NATIVO
          <motion.div
            {...MOBILE_SHEET_ANIMATION}
            onClick={(e) => e.stopPropagation()}
            className={`w-full ${bgSheet} rounded-t-3xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col`}
            style={{ 
              paddingBottom: 'env(safe-area-inset-bottom, 16px)',
              willChange: 'transform',
              transform: 'translateZ(0)'
            }}
          >
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className={`w-10 h-1.5 ${handleBar} rounded-full`} />
            </div>

            <div 
              className="overflow-y-auto flex-1" 
              style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
            >
              {sharedContent}
            </div>
          </motion.div>
        ) : (
          // 🖥️ DESKTOP: MODAL CENTRADO
          <motion.div
            {...DESKTOP_PANEL_ANIMATION}
            onClick={(e) => e.stopPropagation()}
            className={`relative w-full max-w-md ${bgModal} rounded-2xl shadow-2xl overflow-hidden`}
            style={{ willChange: 'transform, opacity', transform: 'translateZ(0)' }}
          >
            {sharedContent}
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );

  return portalize(mounted, modalContent);
}